import {baseFetch, injectCacheTags} from './base';
import {resolveAPIRoute} from '@/lib/routes';
import {Currency, DealMode, DealType} from '@/types';
import {Coin} from '@/types/api/prices';
import type {
  Settings,
  SettingsCoins,
  SettingsBanks,
  SettingsItems,
  SettingsWages,
  SettingsWagesCalculate,
  SettingsCustomerLoyaltyProgram
} from '@/types/api/settings';

export enum SettingsRoutes {
  Settings = 'base.settings',
  Coins = 'base.settings.coins',
  Banks = 'base.settings.banks',
  Items = 'base.settings.items',
  ItemsBlock = 'base.settings.items.block',
  Wages = 'base.settings.wages',
  WagesCalculate = 'base.settings.wages.calculate',
  CustomerLoyaltyProgram = 'base.settings.customer-loyalty-program',
  CustomerLoyaltyProgramKYCGift = 'base.settings.customer-loyalty-program.kyc-gift'
}

export async function getSettings(requestArgs?: RequestInit) {
  return (
    await baseFetch(
      resolveAPIRoute(SettingsRoutes.Settings),
      injectCacheTags([SettingsRoutes.Settings], requestArgs)
    )
  ).data as Settings;
}

export async function getSettingsCoins(requestArgs?: RequestInit) {
  return (
    await baseFetch(
      resolveAPIRoute(SettingsRoutes.Coins),
      injectCacheTags(
        [SettingsRoutes.Settings, SettingsRoutes.Coins],
        requestArgs
      )
    )
  ).data as SettingsCoins[];
}

export async function getSettingsBanks(requestArgs?: RequestInit) {
  return (
    await baseFetch(
      resolveAPIRoute(SettingsRoutes.Banks),
      injectCacheTags(
        [SettingsRoutes.Settings, SettingsRoutes.Banks],
        requestArgs
      )
    )
  ).data as SettingsBanks;
}

export async function getSettingsItemsBlock(requestArgs?: RequestInit) {
  return (
    await baseFetch(
      resolveAPIRoute(SettingsRoutes.ItemsBlock),
      injectCacheTags(
        [
          SettingsRoutes.Settings,
          SettingsRoutes.Items,
          SettingsRoutes.ItemsBlock
        ],
        requestArgs
      )
    )
  ).data as SettingsItems['block'];
}

export async function getSettingsWages(requestArgs?: RequestInit) {
  return (
    await baseFetch(
      resolveAPIRoute(SettingsRoutes.Wages),
      injectCacheTags(
        [SettingsRoutes.Settings, SettingsRoutes.Wages],
        requestArgs
      )
    )
  ).data as SettingsWages;
}

export async function requestSettingsWagesCalculate(
  requestArgs?: Omit<RequestInit, 'body'> & {
    body: {
      section: DealType;
      type: DealMode;
      pair: Currency;
      coin: Coin['id'];
      amount: number;
      price: number;
    };
  }
) {
  return (
    await baseFetch(
      resolveAPIRoute(SettingsRoutes.WagesCalculate),
      injectCacheTags(
        [SettingsRoutes.Settings, SettingsRoutes.WagesCalculate],
        {
          method: 'POST',
          ...requestArgs,
          body: requestArgs?.body as unknown as BodyInit
        }
      )
    )
  ).data as SettingsWagesCalculate;
}

export async function getSettingsCustomerLoyaltyProgram(
  requestArgs?: RequestInit
) {
  return (
    await baseFetch(
      resolveAPIRoute(SettingsRoutes.CustomerLoyaltyProgram),
      injectCacheTags(
        [SettingsRoutes.Settings, SettingsRoutes.CustomerLoyaltyProgram],
        requestArgs
      )
    )
  ).data as SettingsCustomerLoyaltyProgram;
}

export async function getSettingsCustomerLoyaltyProgramKYCGift(
  requestArgs?: RequestInit
) {
  return (
    await baseFetch(
      resolveAPIRoute(SettingsRoutes.CustomerLoyaltyProgramKYCGift),
      injectCacheTags(
        [
          SettingsRoutes.Settings,
          SettingsRoutes.CustomerLoyaltyProgram,
          SettingsRoutes.CustomerLoyaltyProgramKYCGift
        ],
        requestArgs
      )
    )
  ).data as SettingsCustomerLoyaltyProgram['kycGift'];
}
